import React, { useMemo, useState } from 'react';
import { Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { PatternFormat } from 'react-number-format';
import { MoreHoriz } from '@mui/icons-material';
import Table from '../common/Table';

const ManagersTable = ({
    members,
    onMemberClick,
    canManage,
    onRemoveManager,
    onConvertToTenant,
    loading = false
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedManager, setSelectedManager] = useState(null);

    const hasActions = Boolean(canManage && (onRemoveManager || onConvertToTenant));

    const handleMenuClick = (event, member) => {
        if (!hasActions) return;
        setAnchorEl(event.currentTarget);
        setSelectedManager(member);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedManager(null);
    };

    const handleRemove = () => {
        onRemoveManager?.(selectedManager);
        handleMenuClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ row }) => (
                    <>
                        {row.original.first_name} {row.original.last_name}
                        {row.original.company_name && (
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                {row.original.company_name}
                            </Typography>
                        )}
                    </>
                )
            },
            {
                Header: 'Phone',
                accessor: 'phone',
                Cell: ({ row }) => (
                    <Typography sx={{ fontSize: '13px' }}>
                        {row.original?.phone?.number ? (
                            <PatternFormat
                                value={row.original.phone.number}
                                displayType="text"
                                format={`+${row.original.phone.code} (###)-###-####`}
                            />
                        ) : (
                            row.original?.phone || ''
                        )}
                    </Typography>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ row }) =>
                    row.original.status === 'invited' ? (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                display: 'inline-block',
                                bgcolor: 'warning.lighter',
                                color: 'warning.dark',
                                fontWeight: 600
                            }}
                        >
                            Invited
                        </Typography>
                    ) : (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                display: 'inline-block',
                                bgcolor: 'primary.light',
                                color: 'white',
                                fontWeight: 600
                            }}
                        >
                            Active
                        </Typography>
                    )
            },
            ...(hasActions
                ? [
                      {
                          Header: 'Actions',
                          className: 'cell-right',
                          Cell: ({ row }) => (
                              <IconButton
                                  onClick={e => {
                                      e.stopPropagation();
                                      handleMenuClick(e, row.original);
                                  }}
                              >
                                  <MoreHoriz />
                              </IconButton>
                          )
                      }
                  ]
                : [])
        ],
        [hasActions]
    );

    return (
        <>
            <Table columns={columns} data={members} onRowClick={onMemberClick} />

            {hasActions && (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    {onRemoveManager && (
                        <MenuItem disabled={loading} onClick={handleRemove}>
                            Remove Manager
                        </MenuItem>
                    )}
                </Menu>
            )}
        </>
    );
};

export default ManagersTable;
